import React from "react";
import { Wifi, Check, CheckCircle2, Clock, ShieldCheck, Scale, CalendarDays, FilePlus2, Send, Link2, BadgeCheck } from "lucide-react";
import { ASSETS } from "@/data/content";

/* ---------- realistic status bar bits ---------- */
const SignalBars = () => (
  <div className="flex items-end gap-[2px] h-3">
    {[5, 7, 9, 11].map((h, i) => (
      <span key={i} className="w-[3px] rounded-[1px] bg-[#091630]" style={{ height: h }} />
    ))}
  </div>
);

const Battery = () => (
  <div className="flex items-center gap-[2px]">
    <div className="relative h-[13px] w-[23px] rounded-[4px] border-[1.5px] border-[#091630]/70 p-[2px]">
      <div className="h-full w-[78%] rounded-[1.5px] bg-[#091630]" />
    </div>
    <div className="h-[5px] w-[2px] rounded-r-sm bg-[#091630]/60" />
  </div>
);

const StatusBar = () => (
  <div className="relative pt-[16px] px-6 flex items-center justify-between">
    <span className="text-[14px] font-semibold text-[#091630] tracking-tight">9:41</span>
    <div className="flex items-center gap-[6px]">
      <SignalBars />
      <Wifi className="h-[15px] w-[15px] text-[#091630]" strokeWidth={2.4} />
      <Battery />
    </div>
  </div>
);

/* ---------- shared mini UI atoms ---------- */
const AppHeader = ({ title }) => (
  <div className="flex items-center justify-center mb-4">
    <img src={ASSETS.logo} alt="WorkPay" style={{ height: 17, width: "auto" }} />
    {title && <span className="absolute right-5 text-[11px] font-semibold text-slate-400">{title}</span>}
  </div>
);

const Tick = ({ label }) => (
  <div className="flex items-center gap-2 text-[12px] text-slate-600">
    <Check className="h-3.5 w-3.5 text-[#16a34a]" /> {label}
  </div>
);

/* ---------- screens ---------- */
const PaymentScreen = () => (
  <div>
    <AppHeader />
    <div className="flex items-center gap-1.5 mb-1.5">
      <BadgeCheck className="h-4 w-4 text-[#16a34a]" />
      <span className="text-[11px] font-semibold text-slate-500">Pixel Studio · Verified vendor</span>
    </div>
    <h4 className="font-display text-[17px] font-extrabold text-[#091630]">Brand Identity Pack</h4>
    <div className="mt-4 rounded-2xl bg-[#f7faf8] p-4 text-center">
      <p className="text-[11px] text-slate-400">Project total</p>
      <p className="font-display text-[28px] leading-tight font-extrabold text-[#091630]">AED 2,450</p>
    </div>
    <div className="mt-4 space-y-2">
      <Tick label="Scope & deliverables agreed" />
      <Tick label="3 milestones · 6 weeks" />
      <Tick label="Approval before each release" />
    </div>
    <button className="mt-5 w-full bg-[#21c55e] text-[#091630] font-bold py-3.5 rounded-2xl text-[13px]">Pay securely</button>
    <p className="mt-3 text-center text-[10px] text-slate-400">
      Powered by <span className="font-display font-extrabold text-[#635BFF]">stripe</span>
    </p>
  </div>
);

const MilestoneScreen = () => {
  const rows = [
    { n: "Design concept", s: "Approved", c: "#16a34a", b: "#ecfdf3" },
    { n: "Development", s: "In review", c: "#d97706", b: "#fff7ed" },
    { n: "Launch", s: "Pending", c: "#64748b", b: "#f1f5f9" },
  ];
  return (
    <div>
      <AppHeader />
      <p className="text-[11px] text-slate-400">Project</p>
      <h4 className="font-display text-[16px] font-extrabold text-[#091630] mb-3">Website Redesign</h4>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] text-slate-400">Progress</span>
        <span className="text-[11px] font-extrabold text-[#16a34a]">66%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden mb-4">
        <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-[#21c55e] to-[#06b6d4]" />
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.n} className="flex items-center justify-between bg-white border border-slate-100 rounded-2xl px-3.5 py-3">
            <span className="text-[13px] font-medium text-[#091630]">{r.n}</span>
            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ color: r.c, background: r.b }}>{r.s}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2.5 rounded-2xl bg-[#f1ecfe] px-3.5 py-3">
        <CalendarDays className="h-4 w-4 text-[#7c3aed]" />
        <span className="text-[12px] font-semibold text-[#091630]">Daily payout · AED 2,450</span>
      </div>
    </div>
  );
};

const ApprovalScreen = () => (
  <div>
    <AppHeader />
    <div className="h-11 w-11 rounded-full bg-[#ecfdf3] flex items-center justify-center mb-3">
      <CheckCircle2 className="h-6 w-6 text-[#16a34a]" />
    </div>
    <h4 className="font-display text-[16px] font-extrabold text-[#091630]">Approve milestone?</h4>
    <p className="text-[12px] text-slate-500 mt-1">Design concept · Milestone 1 of 3</p>
    <div className="mt-3.5 rounded-2xl border border-slate-100 p-3.5 space-y-2">
      <Tick label="Homepage concept" />
      <Tick label="Brand colors" />
      <Tick label="2 revisions used" />
    </div>
    <button className="mt-4 w-full bg-[#21c55e] text-[#091630] font-bold py-3.5 rounded-2xl text-[13px]">Approve &amp; release</button>
    <button className="mt-2.5 w-full bg-white border border-slate-200 text-slate-600 font-semibold py-3.5 rounded-2xl text-[13px]">Request changes</button>
  </div>
);

const DisputeScreen = () => (
  <div>
    <AppHeader />
    <div className="flex items-center gap-2.5 mb-4">
      <div className="h-11 w-11 rounded-full bg-[#fff7ed] flex items-center justify-center">
        <Scale className="h-6 w-6 text-[#d97706]" />
      </div>
      <div>
        <h4 className="font-display text-[16px] font-extrabold text-[#091630]">Dispute center</h4>
        <span className="text-[11px] font-bold text-[#d97706]">Under review</span>
      </div>
    </div>
    <div className="space-y-2.5">
      {[
        { t: "Issue raised", i: Clock, c: "#d97706" },
        { t: "Evidence submitted", i: Check, c: "#16a34a" },
        { t: "Project timeline shared", i: Check, c: "#16a34a" },
      ].map((r) => (
        <div key={r.t} className="flex items-center gap-2.5 bg-white border border-slate-100 rounded-2xl px-3.5 py-3">
          <r.i className="h-4 w-4" style={{ color: r.c }} />
          <span className="text-[12px] font-medium text-[#091630]">{r.t}</span>
        </div>
      ))}
    </div>
    <div className="mt-3.5 rounded-2xl bg-[#f7faf8] px-3.5 py-3 text-[11px] text-slate-500 leading-relaxed">
      Proof &amp; messages kept organized around the project.
    </div>
  </div>
);

const PayoutScreen = () => (
  <div>
    <AppHeader />
    <div className="flex items-center gap-3 mb-4">
      <div className="h-12 w-12 rounded-2xl bg-[#f1ecfe] flex items-center justify-center">
        <CalendarDays className="h-6 w-6 text-[#7c3aed]" />
      </div>
      <div>
        <p className="text-[11px] text-slate-400">Daily payout</p>
        <p className="font-display text-[18px] font-extrabold text-[#091630]">AED 2,450</p>
      </div>
      <span className="ml-auto text-[10px] font-bold text-[#16a34a] bg-[#ecfdf3] px-2.5 py-1 rounded-full">Today</span>
    </div>
    <div className="space-y-2.5 pt-3 border-t border-slate-100">
      <Tick label="Milestone approved" />
      <Tick label="Eligible for payout" />
      <Tick label="Daily payout flow" />
    </div>
    <div className="mt-4 rounded-2xl bg-[#f7faf8] px-3.5 py-3 text-[11px] text-slate-500 leading-relaxed">
      Approved milestones move to your account on a daily payout basis.
    </div>
  </div>
);

/* ---------- how-it-works step screens ---------- */
const CreateScreen = () => (
  <div>
    <AppHeader />
    <div className="flex items-center gap-2 mb-3">
      <FilePlus2 className="h-4 w-4 text-[#16a34a]" />
      <h4 className="font-display text-[15px] font-extrabold text-[#091630]">New project</h4>
    </div>
    <label className="text-[10px] font-semibold text-slate-400">Project amount</label>
    <div className="mt-1 rounded-2xl border border-slate-100 bg-[#f7faf8] px-3.5 py-3 font-display text-[18px] font-extrabold text-[#091630]">AED 2,450</div>
    <label className="mt-3 block text-[10px] font-semibold text-slate-400">Scope</label>
    <div className="mt-1 rounded-2xl border border-slate-100 px-3.5 py-2.5 text-[12px] text-slate-600">Website redesign · 5 pages</div>
    <div className="mt-3 space-y-2">
      {["Milestone 1 · Design", "Milestone 2 · Build", "Milestone 3 · Launch"].map((m) => (
        <div key={m} className="flex items-center gap-2 bg-white border border-slate-100 rounded-xl px-3 py-2.5 text-[12px] text-[#091630]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#21c55e]" /> {m}
        </div>
      ))}
    </div>
    <button className="mt-4 w-full bg-[#21c55e] text-[#091630] font-bold py-3 rounded-2xl text-[13px]">Create project</button>
  </div>
);

const SendScreen = () => (
  <div>
    <AppHeader />
    <div className="h-11 w-11 rounded-full bg-[#ecfdf3] flex items-center justify-center mb-3">
      <Send className="h-5 w-5 text-[#16a34a]" />
    </div>
    <h4 className="font-display text-[16px] font-extrabold text-[#091630]">Share payment link</h4>
    <p className="text-[12px] text-slate-500 mt-1">Send a professional project page.</p>
    <div className="mt-3.5 flex items-center gap-2 rounded-2xl border border-slate-100 bg-[#f7faf8] px-3.5 py-3">
      <Link2 className="h-4 w-4 text-[#16a34a] shrink-0" />
      <span className="text-[11px] text-slate-500 truncate">workpay.app/pay/redesign</span>
    </div>
    <div className="mt-3 grid grid-cols-2 gap-2.5">
      <button className="bg-[#091630] text-white font-semibold py-2.5 rounded-xl text-[12px]">WhatsApp</button>
      <button className="bg-white border border-slate-200 text-[#091630] font-semibold py-2.5 rounded-xl text-[12px]">Email</button>
    </div>
    <div className="mt-3.5 rounded-2xl bg-[#f7faf8] px-3.5 py-3">
      <Tick label="Customer sees scope & milestones" />
    </div>
  </div>
);

const TrackScreen = () => (
  <div>
    <AppHeader />
    <div className="flex items-center gap-2 mb-3">
      <h4 className="font-display text-[15px] font-extrabold text-[#091630]">Project activity</h4>
      <span className="ml-auto text-[11px] font-extrabold text-[#16a34a]">Live</span>
    </div>
    <div className="space-y-2.5">
      {[
        { t: "Milestone approved", s: "Just now", c: "#16a34a", b: "#ecfdf3" },
        { t: "Update shared", s: "2h ago", c: "#0891b2", b: "#ecfeff" },
        { t: "Payment received", s: "Yesterday", c: "#16a34a", b: "#ecfdf3" },
        { t: "Project started", s: "3 days ago", c: "#64748b", b: "#f1f5f9" },
      ].map((r) => (
        <div key={r.t} className="flex items-center gap-3 bg-white border border-slate-100 rounded-2xl px-3.5 py-3">
          <span className="h-7 w-7 rounded-full flex items-center justify-center shrink-0" style={{ background: r.b }}>
            <Check className="h-3.5 w-3.5" style={{ color: r.c }} />
          </span>
          <div className="min-w-0">
            <p className="text-[12px] font-semibold text-[#091630] leading-tight">{r.t}</p>
            <p className="text-[10px] text-slate-400">{r.s}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ReleaseScreen = () => (
  <div className="text-center">
    <AppHeader />
    <div className="mx-auto h-16 w-16 rounded-full bg-[#ecfdf3] flex items-center justify-center mt-2 mb-4">
      <CheckCircle2 className="h-8 w-8 text-[#16a34a]" />
    </div>
    <h4 className="font-display text-[17px] font-extrabold text-[#091630]">Payment released</h4>
    <p className="text-[12px] text-slate-500 mt-1.5 px-2">Funds move based on the agreed workflow.</p>
    <div className="mt-4 rounded-2xl bg-[#f7faf8] p-4">
      <p className="text-[11px] text-slate-400">Released to vendor</p>
      <p className="font-display text-[26px] font-extrabold text-[#091630] leading-tight">AED 2,450</p>
    </div>
    <div className="mt-4 space-y-2 text-left">
      <Tick label="Customer approved" />
      <Tick label="Daily payout scheduled" />
    </div>
  </div>
);

const SCREENS = {
  milestone: MilestoneScreen,
  payment: PaymentScreen,
  approval: ApprovalScreen,
  dispute: DisputeScreen,
  payout: PayoutScreen,
  create: CreateScreen,
  send: SendScreen,
  track: TrackScreen,
  release: ReleaseScreen,
};

const SideButton = ({ side, top, h }) => (
  <span
    className={`absolute ${side === "left" ? "-left-[2px] rounded-l-[3px]" : "-right-[2px] rounded-r-[3px]"} w-[3px] bg-gradient-to-b from-[#d7d7dd] via-[#9b9ba1] to-[#c4c4cb]`}
    style={{ top, height: h }}
  />
);

/**
 * Realistic "half iPhone" mockup — Dynamic Island, titanium rails, side
 * buttons, real status bar, soft mint blob, cropped at the bottom.
 */
export const PhoneMock = ({ variant = "milestone", className = "" }) => {
  const Screen = SCREENS[variant] || MilestoneScreen;
  return (
    <div className={`relative mx-auto w-[300px] ${className}`} data-testid={`phone-${variant}`}>
      {/* soft mint blob behind */}
      <div className="absolute left-1/2 -translate-x-1/2 top-8 h-[88%] w-[118%] -z-10 rounded-[42%] bg-[#d4f3df] blur-2xl opacity-70" />

      {/* cropped viewport → only the top of the phone shows */}
      <div className="relative h-[470px] overflow-hidden">
        {/* titanium outer rail */}
        <div className="relative w-[300px] rounded-t-[3.4rem] rounded-b-[3.4rem] p-[5px] bg-gradient-to-br from-[#e9e9ee] via-[#a6a6ac] to-[#d2d2d8] shadow-[0_30px_55px_-18px_rgba(9,22,48,0.45)]">
          {/* side buttons */}
          <SideButton side="left" top={132} h={26} />
          <SideButton side="left" top={178} h={52} />
          <SideButton side="left" top={244} h={52} />
          <SideButton side="right" top={206} h={78} />

          {/* inner highlight ring */}
          <div className="rounded-[3rem] p-[2px] bg-gradient-to-br from-[#f6f6f8] to-[#9d9da3]">
            {/* black bezel */}
            <div className="rounded-[2.85rem] bg-[#050505] p-[3px]">
              {/* screen */}
              <div className="relative rounded-[2.65rem] bg-white overflow-hidden h-[600px]">
                {/* Dynamic Island */}
                <div className="absolute top-[14px] left-1/2 -translate-x-1/2 z-20 h-[27px] w-[94px] rounded-full bg-black flex items-center justify-end pr-3">
                  <span className="h-2 w-2 rounded-full bg-[#0a1626] ring-[1.5px] ring-[#16263d]" />
                </div>

                <StatusBar />

                <div className="px-5 pt-5 relative">
                  <Screen />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneMock;
